import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  writeBatch,
  Timestamp,
  increment,
  limit,
  setDoc,
} from "firebase/firestore"
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage"
import { db, storage } from "../firebase/config"

import type { Product } from "../store/slices/productsSlice"
import type { Order } from "../store/slices/ordersSlice"
import type { Category } from "../types"

// Helper function to convert Firebase Timestamp to ISO string
const convertTimestamp = <T>(data: T): T => {
  if (data instanceof Timestamp) {
    return data.toDate().toISOString() as T
  }
  if (Array.isArray(data)) {
    return data.map(convertTimestamp) as T
  }
  if (data && typeof data === 'object' && data !== null) {
    return Object.fromEntries(
      Object.entries(data as Record<string, unknown>).map(([key, value]) => [key, convertTimestamp(value)])
    ) as T
  }
  return data
}

// Products Service
export const productsService = {
  // Get all products
  async getProducts(): Promise<Product[]> {
    try {
      const querySnapshot = await getDocs(collection(db, "products"))
      return querySnapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          ...convertTimestamp(data),
        } as Product
      })
    } catch (error) {
      console.error("Error getting products:", error)
      throw error
    }
  },

  // Get product by ID
  async getProduct(id: string): Promise<Product | null> {
    try {
      const docRef = doc(db, "products", id)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        const data = docSnap.data()
        return { 
          id: docSnap.id, 
          ...convertTimestamp(data)
        } as Product
      }
      return null
    } catch (error) {
      console.error("Error getting product:", error)
      throw error
    }
  },

  // Add new product
  async addProduct(product: Omit<Product, "id">): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, "products"), {
        ...product,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      return docRef.id
    } catch (error) {
      console.error("Error adding product:", error)
      throw error
    }
  },

  // Update product
  async updateProduct(id: string, product: Partial<Product>): Promise<void> {
    try {
      const docRef = doc(db, "products", id)
      await updateDoc(docRef, {
        ...product,
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      console.error("Error updating product:", error)
      throw error
    }
  },

  // Delete product
  async deleteProduct(id: string): Promise<void> {
    try {
      const docRef = doc(db, "products", id)
      await deleteDoc(docRef)
    } catch (error) {
      console.error("Error deleting product:", error)
      throw error
    }
  },

  // Search products
  async searchProducts(searchTerm: string): Promise<Product[]> {
    try {
      const products = await this.getProducts()
      return products.filter(
        (product) =>
          product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
          product.tags.some((tag) => tag.toLowerCase().includes(searchTerm.toLowerCase())),
      )
    } catch (error) {
      console.error("Error searching products:", error)
      throw error
    }
  },

  // Get products by category
  async getProductsByCategory(category: string): Promise<Product[]> {
    try {
      const q = query(collection(db, "products"), where("category", "==", category))
      const querySnapshot = await getDocs(q)
      return querySnapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          ...convertTimestamp(data),
        } as Product
      })
    } catch (error) {
      console.error("Error getting products by category:", error)
      throw error
    }
  },

  // Bulk update products
  async bulkUpdateProducts(updates: { id: string; data: Partial<Product> }[]): Promise<void> {
    try {
      const batch = writeBatch(db)

      updates.forEach(({ id, data }) => {
        const docRef = doc(db, "products", id)
        batch.update(docRef, { ...data, updatedAt: serverTimestamp() })
      })

      await batch.commit()
    } catch (error) {
      console.error("Error bulk updating products:", error)
      throw error
    }
  },
}

// Categories Service
export const categoriesService = {
  // Get all categories
  async getCategories(): Promise<Category[]> {
    try {
      const querySnapshot = await getDocs(collection(db, "categories"))
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Category[]
    } catch (error) {
      console.error("Error getting categories:", error)
      throw error
    }
  },

  // Get category by ID
  async getCategory(id: string): Promise<Category | null> {
    try {
      const docRef = doc(db, "categories", id)
      const docSnap = await getDoc(docRef)

      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as Category
      }
      return null
    } catch (error) {
      console.error("Error getting category:", error)
      throw error
    }
  },

  // Add new category
  async addCategory(category: Omit<Category, "id">): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, "categories"), {
        ...category,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      return docRef.id
    } catch (error) {
      console.error("Error adding category:", error)
      throw error
    }
  },

  // Update category
  async updateCategory(id: string, category: Partial<Category>): Promise<void> {
    try {
      const docRef = doc(db, "categories", id)
      await updateDoc(docRef, {
        ...category,
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      console.error("Error updating category:", error)
      throw error
    }
  },

  // Delete category
  async deleteCategory(id: string): Promise<void> {
    try {
      const docRef = doc(db, "categories", id)
      await deleteDoc(docRef)
    } catch (error) {
      console.error("Error deleting category:", error)
      throw error
    }
  },
}

// Orders Service
export const ordersService = {
  // Get all orders
  async getOrders(): Promise<Order[]> {
    try {
      const ordersRef = collection(db, "orders")
      const q = query(ordersRef, orderBy("createdAt", "desc"))
      const snapshot = await getDocs(q)
      return snapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          ...convertTimestamp(data),
        } as Order
      })
    } catch (error) {
      console.error("Error getting orders:", error)
      throw error
    }
  },

  // Get order by ID
  async getOrder(id: string): Promise<Order | null> {
    try {
      const orderRef = doc(db, "orders", id)
      const orderDoc = await getDoc(orderRef)

      if (orderDoc.exists()) {
        const data = orderDoc.data()
        return {
          id: orderDoc.id,
          ...convertTimestamp(data),
        } as Order
      }
      return null
    } catch (error) {
      console.error("Error getting order:", error)
      throw error
    }
  },

  // Get orders by user
  async getUserOrders(userId: string): Promise<Order[]> {
    try {
      const ordersRef = collection(db, "orders")
      const q = query(ordersRef, where("userId", "==", userId), orderBy("createdAt", "desc"))
      const snapshot = await getDocs(q)
      return snapshot.docs.map((doc) => {
        const data = doc.data()
        return {
          id: doc.id,
          ...convertTimestamp(data),
        } as Order
      })
    } catch (error) {
      console.error("Error getting user orders:", error)
      throw error
    }
  },

  // Add new order
  async addOrder(order: Omit<Order, "id">): Promise<string> {
    try {
      const ordersRef = collection(db, "orders")
      const docRef = await addDoc(ordersRef, {
        ...order,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      return docRef.id
    } catch (error) {
      console.error("Error adding order:", error)
      throw error
    }
  },

  // Update order
  async updateOrder(id: string, order: Partial<Order>): Promise<void> {
    try {
      const orderRef = doc(db, "orders", id)
      await updateDoc(orderRef, {
        ...order,
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      console.error("Error updating order:", error)
      throw error
    }
  },

  // Update order status
  async updateOrderStatus(orderId: string, status: Order["status"]): Promise<void> {
    try {
      const orderRef = doc(db, "orders", orderId)
      await updateDoc(orderRef, {
        status,
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      console.error("Error updating order status:", error)
      throw error
    }
  },

  // Update payment status
  async updatePaymentStatus(id: string, paymentStatus: Order["paymentStatus"]): Promise<void> {
    try {
      const orderRef = doc(db, "orders", id)
      await updateDoc(orderRef, {
        paymentStatus,
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      console.error("Error updating payment status:", error)
      throw error
    }
  },

  // Delete order
  async deleteOrder(id: string): Promise<void> {
    try {
      const orderRef = doc(db, "orders", id)
      await deleteDoc(orderRef)
    } catch (error) {
      console.error("Error deleting order:", error)
      throw error
    }
  },

  // Payment record methods
  async addPaymentRecord(paymentData: any): Promise<string> {
    try {
      const paymentsRef = collection(db, "payments")
      const docRef = await addDoc(paymentsRef, {
        ...paymentData,
        createdAt: serverTimestamp(),
      })
      return docRef.id
    } catch (error) {
      console.error("Error adding payment record:", error)
      throw error
    }
  },

  async getPaymentRecord(merchantTransactionId: string): Promise<any> {
    try {
      const paymentsRef = collection(db, "payments")
      const q = query(paymentsRef, where("merchantTransactionId", "==", merchantTransactionId))
      const snapshot = await getDocs(q)

      if (!snapshot.empty) {
        const doc = snapshot.docs[0]
        return { id: doc.id, ...doc.data() }
      }
      return null
    } catch (error) {
      console.error("Error getting payment record:", error)
      throw error
    }
  },

  async updatePaymentRecord(merchantTransactionId: string, updateData: any): Promise<void> {
    try {
      const paymentRecord = await this.getPaymentRecord(merchantTransactionId)
      if (paymentRecord) {
        const paymentRef = doc(db, "payments", paymentRecord.id)
        await updateDoc(paymentRef, {
          ...updateData,
          updatedAt: serverTimestamp(),
        })
      }
    } catch (error) {
      console.error("Error updating payment record:", error)
      throw error
    }
  },

  async updateOrderPaymentStatus(orderId: string, paymentData: any): Promise<void> {
    try {
      const orderRef = doc(db, "orders", orderId)
      await updateDoc(orderRef, {
        ...paymentData,
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      console.error("Error updating order payment status:", error)
      throw error
    }
  },

  // Revenue analytics methods
  async updateRevenueAnalytics(
    period: "daily" | "monthly" | "total",
    periodKey: string,
    amount: number,
  ): Promise<void> {
    try {
      const analyticsRef = doc(db, "analytics", `revenue_${period}`)
      const analyticsDoc = await getDoc(analyticsRef)

      if (analyticsDoc.exists()) {
        const currentData = analyticsDoc.data()
        await updateDoc(analyticsRef, {
          [`${periodKey}.revenue`]: increment(amount),
          [`${periodKey}.orders`]: increment(1),
          [`${periodKey}.lastUpdated`]: serverTimestamp(),
          totalRevenue: increment(amount),
          totalOrders: increment(1),
        })
      } else {
        await setDoc(analyticsRef, {
          [periodKey]: {
            revenue: amount,
            orders: 1,
            lastUpdated: serverTimestamp(),
          },
          totalRevenue: amount,
          totalOrders: 1,
          createdAt: serverTimestamp(),
        })
      }
    } catch (error) {
      console.error("Error updating revenue analytics:", error)
      throw error
    }
  },

  async getRevenueAnalytics(period: "daily" | "monthly" | "total"): Promise<any> {
    try {
      const analyticsRef = doc(db, "analytics", `revenue_${period}`)
      const analyticsDoc = await getDoc(analyticsRef)

      if (analyticsDoc.exists()) {
        return analyticsDoc.data()
      }
      return null
    } catch (error) {
      console.error("Error getting revenue analytics:", error)
      throw error
    }
  },

  // Refund methods
  async addRefundRecord(refundData: any): Promise<string> {
    try {
      const refundsRef = collection(db, "refunds")
      const docRef = await addDoc(refundsRef, {
        ...refundData,
        createdAt: serverTimestamp(),
      })
      return docRef.id
    } catch (error) {
      console.error("Error adding refund record:", error)
      throw error
    }
  },

  // Razorpay order records
  async addRazorpayOrderRecord(orderData: any): Promise<void> {
    try {
      const razorpayOrdersRef = collection(db, "razorpayOrders")
      await addDoc(razorpayOrdersRef, {
        ...orderData,
        createdAt: serverTimestamp(),
      })
    } catch (error) {
      console.error("Error adding Razorpay order record:", error)
      throw error
    }
  },

  async getRazorpayOrderRecord(razorpayOrderId: string): Promise<any | null> {
    try {
      const razorpayOrdersRef = collection(db, "razorpayOrders")
      const q = query(razorpayOrdersRef, where("razorpayOrderId", "==", razorpayOrderId), limit(1))
      const snapshot = await getDocs(q)
      if (!snapshot.empty) {
        const doc = snapshot.docs[0]
        return { id: doc.id, ...doc.data() }
      }
      return null
    } catch (error) {
      console.error("Error getting Razorpay order record:", error)
      throw error
    }
  },

  async getRefunds(): Promise<any[]> {
    try {
      const refundsRef = collection(db, "refunds")
      const q = query(refundsRef, orderBy("createdAt", "desc"))
      const snapshot = await getDocs(q)
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))
    } catch (error) {
      console.error("Error getting refunds:", error)
      throw error
    }
  },
}

// User Service with saved addresses
export const userService = {
  // Get user profile
  async getUserProfile(uid: string): Promise<any> {
    try {
      const userRef = doc(db, "users", uid)
      const userDoc = await getDoc(userRef)

      if (userDoc.exists()) {
        return { id: userDoc.id, ...userDoc.data() }
      }
      return null
    } catch (error) {
      console.error("Error getting user profile:", error)
      throw error
    }
  },

  // Update user profile
  async updateUserProfile(uid: string, profileData: any): Promise<void> {
    try {
      const userRef = doc(db, "users", uid)
      await updateDoc(userRef, {
        ...profileData,
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      console.error("Error updating user profile:", error)
      throw error
    }
  },

  // Saved addresses methods
  async getSavedAddresses(userId: string): Promise<any[]> {
    try {
      const addressesRef = collection(db, "users", userId, "addresses")
      const q = query(addressesRef, orderBy("createdAt", "desc"))
      const snapshot = await getDocs(q)
      return snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))
    } catch (error) {
      console.error("Error getting saved addresses:", error)
      throw error
    }
  },

  async addSavedAddress(userId: string, addressData: any): Promise<string> {
    try {
      const addressesRef = collection(db, "users", userId, "addresses")

      // If this is set as default, unset other defaults first
      if (addressData.isDefault) {
        await this.unsetAllDefaultAddresses(userId)
      }

      const docRef = await addDoc(addressesRef, {
        ...addressData,
        createdAt: serverTimestamp(),
      })
      return docRef.id
    } catch (error) {
      console.error("Error adding saved address:", error)
      throw error
    }
  },

  async updateSavedAddress(userId: string, addressId: string, addressData: any): Promise<void> {
    try {
      // If this is set as default, unset other defaults first
      if (addressData.isDefault) {
        await this.unsetAllDefaultAddresses(userId)
      }

      const addressRef = doc(db, "users", userId, "addresses", addressId)
      await updateDoc(addressRef, {
        ...addressData,
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      console.error("Error updating saved address:", error)
      throw error
    }
  },

  async deleteSavedAddress(userId: string, addressId: string): Promise<void> {
    try {
      const addressRef = doc(db, "users", userId, "addresses", addressId)
      await deleteDoc(addressRef)
    } catch (error) {
      console.error("Error deleting saved address:", error)
      throw error
    }
  },

  async unsetAllDefaultAddresses(userId: string): Promise<void> {
    try {
      const addressesRef = collection(db, "users", userId, "addresses")
      const q = query(addressesRef, where("isDefault", "==", true))
      const snapshot = await getDocs(q)

      const batch = writeBatch(db)
      snapshot.docs.forEach((doc) => {
        batch.update(doc.ref, { isDefault: false })
      })
      await batch.commit()
    } catch (error) {
      console.error("Error unsetting default addresses:", error)
      throw error
    }
  },

  async getDefaultAddress(userId: string): Promise<any> {
    try {
      const addressesRef = collection(db, "users", userId, "addresses")
      const q = query(addressesRef, where("isDefault", "==", true), limit(1))
      const snapshot = await getDocs(q)

      if (!snapshot.empty) {
        const doc = snapshot.docs[0]
        return { id: doc.id, ...doc.data() }
      }
      return null
    } catch (error) {
      console.error("Error getting default address:", error)
      throw error
    }
  },
}

// Storage Service
export const storageService = {
  // Upload image
  async uploadImage(file: File, path: string): Promise<string> {
    try {
      const storageRef = ref(storage, path)
      const snapshot = await uploadBytes(storageRef, file)
      return await getDownloadURL(snapshot.ref)
    } catch (error) {
      console.error("Error uploading image:", error)
      throw error
    }
  },

  // Delete image
  async deleteImage(url: string): Promise<void> {
    try {
      const imageRef = ref(storage, url)
      await deleteObject(imageRef)
    } catch (error) {
      console.error("Error deleting image:", error)
      throw error
    }
  },

  // Upload multiple images
  async uploadMultipleImages(files: File[], basePath: string): Promise<string[]> {
    try {
      const uploadPromises = files.map(async (file, index) => {
        const path = `${basePath}/${Date.now()}_${index}_${file.name}`
        return this.uploadImage(file, path)
      })

      return await Promise.all(uploadPromises)
    } catch (error) {
      console.error("Error uploading multiple images:", error)
      throw error
    }
  },
}

// Real-time listeners
export const realtimeService = {
  // Listen to products changes
  onProductsChange(callback: (products: Product[]) => void) {
    return onSnapshot(
      collection(db, "products"),
      (snapshot) => {
        const products = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Product[]
        callback(products)
      },
      (error) => {
        console.error("Error listening to products:", error)
      },
    )
  },

  // Listen to orders changes
  onOrdersChange(callback: (orders: Order[]) => void) {
    return onSnapshot(
      query(collection(db, "orders"), orderBy("createdAt", "desc")),
      (snapshot) => {
        const orders = snapshot.docs.map((doc) => {
          const data = doc.data()
          return {
            id: doc.id,
            ...convertTimestamp(data),
          } as Order
        })
        callback(orders)
      },
      (error) => {
        console.error("Error listening to orders:", error)
      },
    )
  },

  // Listen to user orders changes
  onUserOrdersChange(userId: string, callback: (orders: Order[]) => void) {
    return onSnapshot(
      query(collection(db, "orders"), where("userId", "==", userId), orderBy("createdAt", "desc")),
      (snapshot) => {
        const orders = snapshot.docs.map((doc) => {
          const data = doc.data()
          return {
            id: doc.id,
            ...convertTimestamp(data),
          } as Order
        })
        callback(orders)
      },
      (error) => {
        console.error("Error listening to user orders:", error)
      },
    )
  },

  // Listen to categories changes
  onCategoriesChange(callback: (categories: Category[]) => void) {
    return onSnapshot(
      collection(db, "categories"),
      (snapshot) => {
        const categories = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Category[]
        callback(categories)
      },
      (error) => {
        console.error("Error listening to categories:", error)
      },
    )
  },

  // Listen to revenue analytics changes
  onRevenueAnalyticsChange(period: "daily" | "monthly" | "total", callback: (data: any) => void) {
    const analyticsRef = doc(db, "analytics", `revenue_${period}`)

    return onSnapshot(analyticsRef, (doc) => {
      if (doc.exists()) {
        callback(doc.data())
      }
    })
  },
}

// Analytics Service
export const analyticsService = {
  // Get dashboard stats
  async getDashboardStats() {
    try {
      const [products, orders, categories] = await Promise.all([
        this.getProductStats(),
        this.getOrderStats(),
        this.getCategoryStats(),
      ])

      return {
        products,
        orders,
        categories,
      }
    } catch (error) {
      console.error("Error getting dashboard stats:", error)
      throw error
    }
  },

  // Get product statistics
  async getProductStats() {
    try {
      const products = await productsService.getProducts()

      return {
        total: products.length,
        inStock: products.filter((p) => p.stock > 0).length,
        outOfStock: products.filter((p) => p.stock === 0).length,
        lowStock: products.filter((p) => p.stock > 0 && p.stock < 5).length,
        featured: products.filter((p) => p.isFeatured).length,
        newArrivals: products.filter((p) => p.isNewArrival).length,
        bestSellers: products.filter((p) => p.isBestSeller).length,
      }
    } catch (error) {
      console.error("Error getting product stats:", error)
      throw error
    }
  },

  // Get order statistics
  async getOrderStats() {
    try {
      const orders = await ordersService.getOrders()

      const totalRevenue = orders
        .filter((o) => o.paymentStatus === "completed")
        .reduce((sum, order) => sum + order.total, 0)

      return {
        total: orders.length,
        pending: orders.filter((o) => o.status === "pending").length,
        confirmed: orders.filter((o) => o.status === "confirmed").length,
        shipped: orders.filter((o) => o.status === "shipped").length,
        delivered: orders.filter((o) => o.status === "delivered").length,
        cancelled: orders.filter((o) => o.status === "cancelled").length,
        totalRevenue,
        averageOrderValue: orders.length > 0 ? totalRevenue / orders.length : 0,
      }
    } catch (error) {
      console.error("Error getting order stats:", error)
      throw error
    }
  },

  // Get category statistics
  async getCategoryStats() {
    try {
      const categories = await categoriesService.getCategories()
      const products = await productsService.getProducts()

      const categoryStats = categories.map((category) => ({
        id: category.id,
        name: category.name,
        productCount: products.filter((p) => p.category === category.slug).length,
      }))

      return {
        total: categories.length,
        categoryStats,
      }
    } catch (error) {
      console.error("Error getting category stats:", error)
      throw error
    }
  },
}